/**
 * Team Management Page
 * Manage billing company members, view seat usage, and enforce limits
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';

interface CompanyMember {
  id: string;
  user_auth_id: string;
  user_email: string;
  role: string;
  status: string;
  joined_at: string;
}

interface CompanyInfo {
  id: string;
  name: string;
  seat_limit: number;
  seat_used: number;
  status: string;
  plan_id: string;
}

const TeamManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.explanatum.co.uk';

  useEffect(() => {
    fetchCompanyInfo();
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildUrlWithParams = (url: string, params: Record<string, string | undefined>) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.append(key, value);
      }
    });
    const queryString = searchParams.toString();
    return queryString ? `${url}?${queryString}` : url;
  };

  const parseJson = async (response: Response) => {
    try {
      return await response.json();
    } catch (error) {
      console.warn('Failed to parse JSON response', error);
      return {};
    }
  };

  const fetchCompanyInfo = async () => {
    const url = buildUrlWithParams(`${API_URL}/api/billing-company/me`, {
      userId: user?.id || undefined,
    });

    try {
      const response = await fetch(url);

      if (response.status === 404) {
        setCompanyInfo(null);
        return;
      }

      if (!response.ok) {
        const body = await parseJson(response);
        throw new Error(body?.message || 'Failed to fetch company info');
      }

      const data = await response.json();
      if (data?.hasCompany) {
        setCompanyInfo(data.company);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
      toast.error('Failed to load company information');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    const url = buildUrlWithParams(`${API_URL}/api/billing-company/members`, {
      userId: user?.id || undefined,
    });

    try {
      const response = await fetch(url);

      if (response.status === 404) {
        setMembers([]);
        return;
      }

      if (!response.ok) {
        const body = await parseJson(response);
        throw new Error(body?.message || 'Failed to fetch members');
      }

      const data = await response.json();
      setMembers(data?.members || []);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to load team members');
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setInviting(true);
    try {
      const response = await fetch(`${API_URL}/api/billing-company/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: inviteEmail,
          role: inviteRole,
          userId: user?.id,
        }),
      });

      const body = await parseJson(response);

      if (!response.ok) {
        throw new Error(body?.message || body?.error || 'Failed to add member');
      }

      toast.success(body?.message || 'Member added successfully');
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      fetchMembers();
      fetchCompanyInfo(); // Refresh seat count
    } catch (error) {
      console.error('Error inviting member:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to add member');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberEmail: string) => {
    if (!window.confirm(`Are you sure you want to remove ${memberEmail}?`)) {
      return;
    }

    try {
      const url = buildUrlWithParams(`${API_URL}/api/billing-company/members/${memberId}`, {
        userId: user?.id || undefined,
      });

      const response = await fetch(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const body = await parseJson(response);
        throw new Error(body?.message || 'Failed to remove member');
      }

      toast.success('Member removed successfully');
      fetchMembers();
      fetchCompanyInfo(); // Refresh seat count
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to remove member');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading team information...</p>
        </div>
      </div>
    );
  }

  if (!companyInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🏢</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Team Found</h2>
          <p className="text-gray-600 mb-6">
            You're not part of a company subscription. Individual accounts don't have team management features.
          </p>
          <a
            href="/company/signup"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
          >
            Upgrade to Company Plan
          </a>
        </div>
      </div>
    );
  }

  const seatUsagePercentage = (companyInfo.seat_used / companyInfo.seat_limit) * 100;
  const seatsAvailable = companyInfo.seat_limit - companyInfo.seat_used;
  const canAddMembers = seatsAvailable > 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{companyInfo.name}</h1>
              <p className="text-gray-600 mt-1">Plan: {companyInfo.plan_id.toUpperCase()}</p>
            </div>
            <div className={`px-4 py-2 rounded-full text-sm font-medium ${
              companyInfo.status === 'active' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {companyInfo.status === 'active' ? '✓ Active' : companyInfo.status}
            </div>
          </div>
        </div>

        {/* Seat Usage */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Seat Usage</h2>
            <span className="text-2xl font-bold text-gray-900">
              {companyInfo.seat_used} / {companyInfo.seat_limit}
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
            <div
              className={`h-4 rounded-full transition-all ${
                seatUsagePercentage >= 100 ? 'bg-red-600' : 
                seatUsagePercentage >= 80 ? 'bg-yellow-500' : 
                'bg-green-500'
              }`}
              style={{ width: `${Math.min(seatUsagePercentage, 100)}%` }}
            ></div>
          </div>
          
          <p className="text-sm text-gray-600">
            {seatsAvailable > 0 ? (
              <span className="text-green-600">✓ {seatsAvailable} seat{seatsAvailable !== 1 ? 's' : ''} available</span>
            ) : (
              <span className="text-red-600">⚠ No seats available - upgrade to add more members</span>
            )}
          </p>
        </div>

        {/* Team Members */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Team Members</h2>
            <button
              onClick={() => setShowInviteModal(true)}
              disabled={!canAddMembers}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                canAddMembers
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {canAddMembers ? '+ Invite Member' : 'No Seats Available'}
            </button>
          </div>

          {members.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No team members yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {members.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {member.user_email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          {member.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          member.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(member.joined_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        {member.role !== 'owner' && (
                          <button
                            onClick={() => handleRemoveMember(member.id, member.user_email)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Invite Team Member</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="colleague@example.com"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="sales">Sales</option>
                  <option value="finance">Finance</option>
                  <option value="marketing">Marketing</option>
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInviteMember}
                  disabled={inviting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {inviting ? 'Inviting...' : 'Invite'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
